<template>
  <FloatingPopup
    ref="floatingPopup"
    :modelValue="modelValue"
    :placement="placement"
    :offset="offset"
    :width="width"
    :minWidthByTrigger="minWidthByTrigger"
    :teleport="teleport"
    :teleportTo="teleportTo"
    :closeOnOutsideClick="closeOnOutsideClick"
    :closeOnEscape="closeOnEscape"
    :closeOnScroll="closeOnScroll"
    :closeOnResize="closeOnResize"
    :popupClass="popupClass"
    :popupStyle="popupStyle"
    @update:modelValue="$emit('update:modelValue', $event)"
    @open="$emit('open')"
    @close="$emit('close')"
  >
    <template #trigger="{isOpen, open, close, toggle}">
      <slot
        name="trigger"
        :isOpen="isOpen"
        :open="open"
        :close="close"
        :toggle="toggle"
      />
    </template>

    <template #default="{close}">
      <ListPopup
        :listAction="actions"
        :closeOnSelect="closeOnSelect"
        @close="close"
        @select="$emit('select', $event)"
        @change="$emit('change', $event)"
        @reorder="$emit('reorder', $event)"
      >
        <template v-if="$slots.button" #button="slotProps">
          <slot name="button" v-bind="slotProps" />
        </template>
      </ListPopup>
    </template>
  </FloatingPopup>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";

import FloatingPopup from "@/elements/UI/popup/FloatingPopup/FloatingPopup.vue";
import ListPopup from "@/elements/UI/popup/ListPopup/ListPopup.vue";

export default defineComponent({
  name: "ActionMenu",

  components: {
    FloatingPopup,
    ListPopup
  },

  emits: {
    "update:modelValue": (value: boolean): boolean => {
      return typeof value === "boolean";
    },
    open: (): boolean => true,
    close: (): boolean => true,
    select: (payload: ListSelectPayload): boolean => {
      return !!payload.item && typeof payload.index === "number";
    },
    change: (list: ListActionItem[]): boolean => {
      return Array.isArray(list);
    },
    reorder: (payload: ListReorderPayload): boolean => {
      return (
        typeof payload.fromIndex === "number" &&
        typeof payload.toIndex === "number" &&
        Array.isArray(payload.list)
      );
    }
  },

  props: {
    /** Управляет открытым/закрытым состоянием меню через v-model. */
    modelValue: {
      type: Boolean,
      default: false
    },

    /** Список действий, которые будут отрисованы внутри ListPopup. */
    actions: {
      type: Array as PropType<ListActionItem[]>,
      default: (): ListActionItem[] => []
    },

    /** Положение меню относительно trigger-элемента. */
    placement: {
      type: String as PropType<PopupPlacement>,
      default: "bottom-start"
    },

    /** Отступ в пикселях между trigger-элементом и меню. */
    offset: {
      type: Number,
      default: 8
    },

    /** Фиксированная ширина меню: число преобразуется в px, строка используется как есть. */
    width: {
      type: [String, Number] as PropType<string | number>,
      default: ""
    },

    /** Делает минимальную ширину меню равной ширине trigger-элемента. */
    minWidthByTrigger: {
      type: Boolean,
      default: false
    },

    /** Переносит меню через Teleport, чтобы оно не обрезалось родителями с overflow. */
    teleport: {
      type: Boolean,
      default: true
    },

    /** CSS-селектор контейнера, куда будет перенесено меню при teleport === true. */
    teleportTo: {
      type: String,
      default: "body"
    },

    /** Закрывает меню после выбора элемента списка. */
    closeOnSelect: {
      type: Boolean,
      default: true
    },

    /** Закрывает меню при клике вне trigger-элемента и вне самого меню. */
    closeOnOutsideClick: {
      type: Boolean,
      default: true
    },

    /** Закрывает меню при нажатии клавиши Escape. */
    closeOnEscape: {
      type: Boolean,
      default: true
    },

    /** Закрывает меню при скролле; если false — пересчитывает позицию. */
    closeOnScroll: {
      type: Boolean,
      default: true
    },

    /** Закрывает меню при изменении размера окна; если false — пересчитывает позицию. */
    closeOnResize: {
      type: Boolean,
      default: true
    },

    /** Дополнительный класс или набор классов для элемента меню. */
    popupClass: {
      type: [String, Array, Object],
      default: ""
    },

    /** Дополнительные inline-стили меню, применяются поверх вычисленных стилей. */
    popupStyle: {
      type: Object as PropType<Record<string, string | number>>,
      default: (): Record<string, string | number> => ({})
    }
  }
});
</script>
